import React, {useState} from 'react';
import moment from 'moment-timezone';
import omit from 'lodash/omit';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import ChoiceField from '../common/ChoiceField';
import CustomBox from '../common/CustomBox';
import TimeSlot from '../availabilities/TimeSlot';
import ScheduleDayPicker from './ScheduleDayPicker';
import { 
  useGetProviderAvailabilitiesQuery
} from '../../redux/services/providerAvailabilityApi';
import { useGetSessionTypesQuery } from '../../redux/services/sessionTypeApi';
import { useGetSchedulesQuery } from '../../redux/services/scheduleApi';
import { getLocationTypes } from '../../helpers/selectOptions';
import { sortTimeSlotsChronologically } from '../../helpers/scheduling';

const PatientSchedulingTimeForm = ({
  providerId,
  serviceCategory,
  lat,
  lon,
  distance = 30,
  onSubmit,
  hideLocationTypes = false,
  defaultDate = new Date(),
  includeLocation = false,
  onScheduleManually = () => {},
  onSendMessage = null,
}) => {
  const [date, setDate] = useState(defaultDate);
  const [timeSlot, setTimeSlot] = useState(null);
  const [location, setLocation] = useState(null);
  const [availabilityId, setAvailabilityId] = useState(null);
  const filter = {
    provider_id: providerId,
    service_category: serviceCategory,
    lat,
    lon,
    day: moment(date).format('ddd').toUpperCase(),
    distance,
    start_minute__gte: 1,
    end_minute__lte: 23 * 60 + 59,
    include_nonpublic: true,
  };
  const paWeekQuery = useGetProviderAvailabilitiesQuery(
    omit(filter, 'day'),
    {skip: !providerId || !serviceCategory}
  );
  const weekAvailabilities = paWeekQuery?.data?.data || [];
  const paDayQuery = useGetProviderAvailabilitiesQuery(
    filter, 
    {skip: !providerId || !serviceCategory}
  );
  const dayAvailabilities = paDayQuery?.data?.data || [];
  const timezone = dayAvailabilities?.[0]?.provider?.timezone || moment.tz.guess();
  const slotQuery = useGetSchedulesQuery({
    provider_id: providerId,
    service_category: serviceCategory,
    day: moment.tz(date, timezone).format('ddd').toUpperCase(),
    lat,
    lon,
    distance,
    start_time: moment.tz(date, timezone).startOf('day').toISOString(),
    end_time: moment.tz(date, timezone).endOf('day').toISOString(),
  }, {skip: !dayAvailabilities?.[0]});
  const sessionTypeQuery = useGetSessionTypesQuery({
    provider_id: providerId,
    session_type_version: '2.0',
  }, {skip: !providerId || !serviceCategory});
  const sessionTypes = sessionTypeQuery?.data || [];
  const sessionType = (
    sessionTypes.find(st => st.service_category === serviceCategory) 
    || null
  );
  const slots = sortTimeSlotsChronologically(slotQuery?.data?.data || []).map(a => ({
    availability_id: a.provider_availability_id,
    start_time: a.start_time,
    end_time: a.end_time,
    provider_availability: a.provider_availability,
  }));
  const selectedAvailability = dayAvailabilities
    .find(a => a.id === timeSlot?.availability_id);
  const config = (selectedAvailability?.configs || [])
    .find(c => c?.session_type?.service_category === serviceCategory);
  const locationOptions = [
    ...(config?.location_types || []).map(lt => ({
      label: getLocationTypes().find(opt => opt.value === lt)?.label,
      value: lt,
    })),
  ];
  const isoString = timeSlot?.start_time || null;
  return (
    <Box>
      <Stack spacing={2}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <IconButton
            size='small'
            color='primary'
            onClick={() => setDate(moment(date).subtract(1, 'week').toDate())}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant='body1' fontWeight={700}>
            {moment(date).startOf('week').format('MMM D')}
            -
            {moment(date).endOf('week').format('MMM D')}
          </Typography>
          <IconButton
            size='small'
            color='primary'
            onClick={() => setDate(moment(date).add(1, 'week').toDate())}
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
        <Stack direction='row' alignItems='center' justifyContent='center'>
          <ScheduleDayPicker 
            date={date} 
            spacing={0.5}
            onChange={(e, date) => setDate(date)}
            availabilities={weekAvailabilities} 
            allowPastDays={false}
          />
        </Stack>
      </Stack>
      <Box style={{minHeight: '200px'}} py={4} px={2}>
        <Stack spacing={2}>
          <Stack spacing={1.5}>
            <Typography variant='h3'>Available Times</Typography>
            <Divider />
          </Stack>
          {/* <Stack direction='row' spacing={2} flexWrap='wrap'> */}
          <Grid container gap={1}>
            {slots.map(_s => (
              <Grid item key={_s.id} md='auto'>
                <TimeSlot
                  key={_s.id}
                  timeSlot={_s}
                  selected={timeSlot?.start_time === _s.start_time}
                  onClick={(e, _timeSlot) => {
                    setTimeSlot(_timeSlot);
                    setAvailabilityId(_s.provider_availability.id);
                  }}
                />
              </Grid>
            ))}
          </Grid>
          {/* </Stack> */}
          {(!slots || slots.length === 0) &&
            <CustomBox>
              <Stack p={2}>
                <Typography variant='h3'>No appointment times listed?</Typography>
                <Typography variant='body1' mt={1} mb={1}>
                  Skip scheduling for now and coordinate directly with the provider.
                </Typography>
                <Button 
                  variant='contained'
                  onClick={() => 
                    onSendMessage 
                    ? onSendMessage() 
                    : onScheduleManually()
                  }
                >
                  Coordinate Directly
                </Button>
              </Stack>
            </CustomBox>
          }
          {!!timeSlot && !hideLocationTypes &&
            <Stack spacing={1.5}>
              <Typography variant='h3'>Location</Typography>
              <Divider />
              <ChoiceField
                label={null}
                options={locationOptions}
                onChange={(e, value) => setLocation(value)}
              />
            </Stack>
          }
          <Button
            variant='contained'
            color='primary'
            disabled={!timeSlot}
            onClick={(e) => onSubmit(
              e, 
              {
                providerId,
                providerAvailabilityId: availabilityId,
                sessionTypeId: sessionType?.id,
                isoString,
                location,
              }
            )}
          >
            Continue
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default PatientSchedulingTimeForm;